/**
 * useAuth.jsx — Supabase auth hook (single-user deployment).
 *
 * Supabase is the source of truth: it persists its own session under
 * `sb-<project-ref>-auth-token` in localStorage, auto-refreshes tokens,
 * and emits `onAuthStateChange` events. We mirror its session into
 * React state and forward the bearer token to `lib/api.js` so axios
 * carries it on every `/api/*` call.
 *
 * Public signup is intentionally disabled — UI has no signup form and
 * the backend `/auth/signup` endpoint rejects when `DISABLE_SIGNUP=true`.
 *
 * Surface (stable):
 *   loading, signingOut, session, profile, authRequired,
 *   isAuthenticated, isAdmin, isApproved,
 *   signIn(email, password) → profile
 *   signOut() → void
 *   refreshProfile() → void
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase, supabaseEnabled } from "@/lib/supabase";
import { setAuthToken } from "@/lib/api";
import axios from "axios";
import { toast } from "sonner";

const AuthCtx = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ANONYMOUS = { id: "anonymous", email: "anonymous@local", role: "admin", is_anonymous: true };

// Synchronous storage purge — wipes Supabase auth artifacts AND legacy
// `mp_auth_token` from the previous local-auth experiment. Used by
// signOut and exposed on `window.__hardLogout` as a devtools escape
// hatch.
export function hardPurgeAuthStorage() {
  const purge = (storage) => {
    try {
      for (let i = storage.length - 1; i >= 0; i--) {
        const k = storage.key(i);
        if (!k) continue;
        if (
          k === "mp_auth_token" ||
          k.startsWith("sb-") ||
          k.includes("supabase") ||
          k.includes("auth-token")
        ) {
          storage.removeItem(k);
        }
      }
    } catch {}
  };
  purge(window.localStorage);
  purge(window.sessionStorage);
  try {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .getRegistrations?.()
        .then((rs) => rs.forEach((r) => r.unregister()))
        .catch(() => {});
    }
  } catch {}
}

if (typeof window !== "undefined") {
  window.__hardLogout = () => {
    hardPurgeAuthStorage();
    try { window.localStorage.clear(); } catch {}
    try { window.sessionStorage.clear(); } catch {}
    window.location.replace("/login");
  };
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [authRequired, setAuthRequired] = useState(false);
  const sessionRef = useRef(null);
  const handlingExpiry = useRef(false);

  // Discover whether the backend requires auth at all.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/auth/status`);
        if (!cancelled) setAuthRequired(Boolean(r.data?.auth_required));
      } catch {
        if (!cancelled) setAuthRequired(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Bare-axios request interceptor: attach Bearer for /api/* requests.
  useEffect(() => {
    const id = axios.interceptors.request.use((cfg) => {
      const url = cfg.url || "";
      if (url.startsWith(API)) {
        const tok = sessionRef.current?.access_token;
        if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
      }
      return cfg;
    });
    return () => axios.interceptors.request.eject(id);
  }, []);

  // Bare-axios response interceptor: forced sign-out on 401/disabled-403.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const cfg = err?.config || {};
        const url = cfg.url || "";
        const status = err?.response?.status;
        const isApi = url.startsWith(API);
        const isAuthEndpoint =
          url.includes("/auth/login") ||
          url.includes("/auth/signup") ||
          url.includes("/auth/status");
        const detail = err?.response?.data?.detail;
        if (
          isApi &&
          !isAuthEndpoint &&
          (status === 401 || (status === 403 && /disabled/i.test(String(detail || ""))))
        ) {
          if (sessionRef.current && !handlingExpiry.current) {
            handlingExpiry.current = true;
            const msg =
              status === 401
                ? "Your session expired. Please sign in again."
                : "Your account has been disabled. Please contact an administrator.";
            try { toast.error(msg); } catch {}
            sessionRef.current = null;
            setSession(null);
            setProfile(null);
            setAuthToken(null);
            try { supabase?.auth?.signOut?.(); } catch {}
            setTimeout(() => { handlingExpiry.current = false; }, 1500);
          }
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // Mirror access_token into lib/api.js's axios client.
  useEffect(() => {
    setAuthToken(session?.access_token || null);
  }, [session]);

  // Bootstrap from Supabase + subscribe to auth state changes.
  useEffect(() => {
    if (!supabaseEnabled) {
      // No Supabase configured. If backend doesn't require auth, treat as
      // anonymous-admin so the dev console still renders.
      setProfile(ANONYMOUS);
      setLoading(false);
      return;
    }
    let unsub;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        sessionRef.current = data.session || null;
        setSession(data.session || null);
        if (data.session) {
          await refreshProfile();
        } else {
          setProfile(null);
        }
      } catch (e) {
        console.warn("[auth] getSession failed", e);
      } finally {
        setLoading(false);
      }
      const sub = supabase.auth.onAuthStateChange(async (_evt, s) => {
        sessionRef.current = s || null;
        setSession(s || null);
        if (s) await refreshProfile();
        else setProfile(null);
      });
      unsub = sub.data?.subscription;
    })();
    return () => { try { unsub?.unsubscribe?.(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshProfile = async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      setProfile(r.data);
    } catch {
      setProfile(null);
    }
  };

  const signIn = async (email, password) => {
    // Hit the backend so the same code path that mints/refreshes a profile
    // also fires on first login. The backend internally calls Supabase's
    // anon client and returns { access_token, refresh_token, user }.
    const r = await axios.post(`${API}/auth/login`, { email, password });
    const data = r.data;
    if (!supabaseEnabled) {
      // Shouldn't happen in this deploy (Supabase env always present), but
      // fall back to manual state so the UI doesn't deadlock if it does.
      sessionRef.current = { access_token: data.access_token, refresh_token: data.refresh_token };
      setSession(sessionRef.current);
      setProfile(data.user);
      return data.user;
    }
    try {
      await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      const got = (await supabase.auth.getSession()).data.session;
      sessionRef.current = got;
      setSession(got);
    } catch (err) {
      console.warn("[auth] supabase.setSession failed; using manual state", err);
      sessionRef.current = { access_token: data.access_token, refresh_token: data.refresh_token };
      setSession(sessionRef.current);
    }
    setProfile(data.user);
    refreshProfile().catch(() => {});
    return data.user;
  };

  const signOut = async () => {
    setSigningOut(true);
    sessionRef.current = null;
    setSession(null);
    setProfile(null);
    setAuthToken(null);
    handlingExpiry.current = false;
    hardPurgeAuthStorage();
    if (supabaseEnabled) {
      Promise.resolve()
        .then(() => supabase.auth.signOut())
        .catch((e) => console.warn("[auth] supabase.signOut error", e));
    }
    axios.post(`${API}/auth/logout`).catch(() => {});
    setSigningOut(false);
  };

  const value = useMemo(
    () => ({
      loading,
      signingOut,
      session,
      profile,
      authRequired,
      isAuthenticated: authRequired ? Boolean(profile && !profile.is_anonymous) : true,
      isAdmin: profile?.role === "admin",
      isApproved: Boolean(profile?.approved) || profile?.role === "admin" || !authRequired,
      signIn,
      signOut,
      refreshProfile,
    }),
    [loading, signingOut, session, profile, authRequired],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => {
  const v = useContext(AuthCtx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
};
